import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { PlayCircle, Clock, ChevronRight, Maximize2, Play, X, RotateCcw } from 'lucide-react';
import { listResume, clearResume } from '../utils/resume';
import useCoverPool, { pickCover as pickPoolCover, poolCount } from '../hooks/useCoverPool';

const formatRemaining = (position, duration) => {
  if (!duration || duration <= position) return '';
  const left = duration - position;
  const m = Math.floor(left / 60);
  return m > 0 ? `${m}m left` : `${Math.floor(left)}s left`;
};

const formatTime = (sec) => {
  if (!sec) return '0:00';
  const m = Math.floor(sec / 60);
  const s = Math.floor(sec % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

/**
 * Compact Continue Listening strip that lives on the homepage.
 * Clicking the small expand icon opens a full modal ("expanded view") that
 * shows the larger card layout, matches the old ContinueListeningRow.
 */
const ContinueListening = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  // PERMANENT RULE 20 (Feb 2026): use the curated speaker_cover_images
  // pool for every home-grid / favorites / history tile. Waiting for
  // `ready` ensures the pool has loaded before we resolve covers.
  const { ready: poolReady } = useCoverPool();
  const coverFor = (it) => {
    if (poolReady && poolCount(it.speaker || '') >= 1) {
      const pooled = pickPoolCover(it.speaker || '', it.drive_file_id || it.id || '');
      if (pooled) return pooled;
    }
    return it.cover_image;
  };
  // Feb-2026: allow user to dismiss the whole strip for this session.
  const [dismissed, setDismissed] = useState(() => {
    try { return sessionStorage.getItem('continue_dismissed') === '1'; } catch { return false; }
  });

  useEffect(() => {
    setItems(listResume(20));
  }, []);

  useEffect(() => {
    if (!expanded) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') setExpanded(false); };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener('keydown', onKey);
    };
  }, [expanded]);

  if (items.length === 0 || dismissed) return null;

  const dismissStrip = () => {
    setDismissed(true);
    try { sessionStorage.setItem('continue_dismissed', '1'); } catch { /* ignore */ }
  };

  const removeItem = (id) => {
    clearResume(id);
    setItems(listResume(20));
  };

  return (
    <section className="mb-6" data-testid="continue-listening">
      <div className="flex items-center gap-2 mb-1.5 px-1">
        <PlayCircle className="w-3.5 h-3.5 text-[#9a7b24]" />
        <h3 className="text-[10px] uppercase tracking-[0.16em] font-semibold text-[#9a7b24]">
          Continue Listening
        </h3>
        <button
          type="button"
          onClick={() => setExpanded(true)}
          className="ml-1 p-1 rounded text-[#9a7b24] hover:text-[#0f1729] hover:bg-white transition-colors dark:hover:text-white dark:hover:bg-white/5"
          aria-label="Expand Continue Listening"
          data-testid="continue-expand-btn"
          title="Expand"
        >
          <Maximize2 className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={() => navigate('/history')}
          className="ml-auto flex items-center gap-1 px-2 rounded text-[11px] font-semibold text-[#9a7b24] hover:text-[#0f1729] hover:bg-white transition-colors dark:hover:text-white dark:hover:bg-white/5"
          data-testid="continue-see-all"
        >
          All history
          <ChevronRight className="w-3 h-3" />
        </button>
        <button
          type="button"
          onClick={dismissStrip}
          className="ml-1 p-1 rounded text-[#9a7b24] hover:text-red-600 hover:bg-white transition-colors dark:hover:bg-white/5"
          aria-label="Hide Continue Listening"
          data-testid="continue-dismiss-strip"
          title="Hide until next visit"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <div className="flex items-stretch gap-1.5 overflow-x-auto scrollbar-none pb-1">
        {items.slice(0, 12).map((it) => (
          <div
            key={it.id}
            className="group relative shrink-0 w-[220px] rounded-lg bg-white border border-[#e1ddd6] hover:border-[#9a7b24]/60 hover:shadow-md transition-all dark:bg-[hsl(222,40%,14%)] dark:border-white/10"
            data-testid={`continue-item-${it.id}`}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
              className="absolute top-1 right-1 z-10 w-4 h-4 rounded-full bg-white/95 hover:bg-red-500 hover:text-white text-slate-400 shadow border border-slate-200 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
              aria-label="Remove from Continue Listening"
              data-testid={`continue-remove-${it.id}`}
              title="Remove from Continue Listening"
            >
              <X className="w-2.5 h-2.5" />
            </button>
            <button
              type="button"
              onClick={() => navigate(`/player/${it.id}`)}
              className="w-full text-left"
              title={`${it.title} — ${it.speaker}`}
            >
              <div className="px-2.5 py-2">
                <p className="text-[12px] font-semibold text-[#0f1729] dark:text-white truncate leading-tight">
                  {it.title}
                </p>
                <p className="hidden sm:block text-[10px] text-[#8a8f99] truncate mt-0.5">
                  {it.speaker || 'Unknown'}
                </p>
                <div className="mt-1.5 flex items-center gap-1.5">
                  <div className="flex-1 h-1 rounded-full bg-[#e1ddd6] dark:bg-white/10 overflow-hidden">
                    <div className="h-full bg-[#9a7b24]" style={{ width: `${it.percent || 0}%` }} />
                  </div>
                  <span className="text-[9px] text-[#8a8f99] tabular-nums shrink-0 flex items-center gap-0.5">
                    <Clock className="w-2.5 h-2.5" />
                    {formatRemaining(it.position, it.duration) || `${it.percent}%`}
                  </span>
                </div>
              </div>
            </button>
          </div>
        ))}
      </div>

      {expanded && createPortal(
        <div
          className="fixed inset-0 z-[10000] bg-black/70 backdrop-blur-sm flex items-start justify-center pt-[4vh] pb-8 px-4"
          onClick={() => setExpanded(false)}
          data-testid="continue-expanded-backdrop"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="relative w-full max-w-6xl max-h-full flex flex-col bg-[#ffffff] rounded-2xl border border-[#e5e0d5] shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between gap-2 px-5 py-3 border-b border-[#e5e0d5] bg-white">
              <div className="flex items-center gap-2 min-w-0">
                <RotateCcw className="w-5 h-5 text-[#9a7b24] shrink-0" />
                <h2 className="text-[#0f1729] text-lg md:text-xl font-semibold truncate">
                  Continue Listening
                </h2>
                <span className="text-[#8a8f99] text-xs">{items.length} in progress</span>
              </div>
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="p-2 rounded-md text-[#0f1729] hover:bg-[#ffffff]"
                aria-label="Close"
                data-testid="continue-expanded-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="overflow-y-auto p-4 md:p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {items.map((it) => (
                  <div
                    key={it.id}
                    className="group relative bg-white rounded-xl overflow-hidden border border-[#e1ddd6] hover:border-[#9a7b24] transition-colors cursor-pointer"
                    onClick={() => { setExpanded(false); navigate(`/player/${it.id}`); }}
                    data-testid={`continue-expanded-item-${it.id}`}
                  >
                    <div className="relative w-full h-32 bg-gradient-to-br from-[#0f1729] to-[#001f2a]">
                      {(() => { const cov = coverFor(it); return cov && cov.startsWith('http') && (
                        <img loading="lazy" decoding="async" src={cov} alt="" className="absolute inset-0 w-full h-full object-cover" />
                      ); })()}
                      <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-12 h-12 rounded-full bg-[#9a7b24] flex items-center justify-center">
                          <Play className="w-6 h-6 text-white ml-0.5" fill="currentColor" />
                        </div>
                      </div>
                      <div className="absolute bottom-0 inset-x-0 h-1 bg-black/50">
                        <div className="h-full bg-[#9a7b24]" style={{ width: `${it.percent || 0}%` }} />
                      </div>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); removeItem(it.id); }}
                        className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/60 hover:bg-black/90 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        aria-label="Remove"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                    <div className="p-3">
                      <p className="text-[#0f1729] text-sm font-medium line-clamp-1">{it.title || 'Untitled'}</p>
                      <p className="text-[#8a8f99] text-xs line-clamp-1">{it.speaker || ''}</p>
                      <p className="text-[#4b5563] text-xs mt-1 tabular-nums">
                        {formatTime(it.position)} / {formatTime(it.duration)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}
    </section>
  );
};

export default ContinueListening;
